// NumberInput (DESIGN §3.6.3, Varianten number/currency/percent): rechtsbündig, `tabular-nums`,
// Einheit als Suffix `fg-secondary` (AA auf `input`), Geld mit Währungs-Präfix und Wert in Minor Units (SPEC §0).
// Freie Eingabe wird beim Tippen geparst und beim Verlassen formatiert (Intl, en-GB).
import {
  useState,
  type FocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type Ref,
} from 'react';
import { fieldFrame, fieldInput, type FieldSize } from './_lib/fieldStyles';
import { useFieldProps } from './_lib/fieldContext';
import {
  clamp,
  currencySymbol,
  formatMinorPlain,
  formatPlain,
  fromMinor,
  parseNumberInput,
  toMinor,
} from './_lib/number';
import { cn } from '../basis/kern';

export type NumberKind = 'number' | 'currency' | 'percent';

export interface NumberInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type' | 'value' | 'defaultValue' | 'onChange' | 'min' | 'max' | 'step'
> {
  ref?: Ref<HTMLInputElement>;
  kind?: NumberKind;
  /** number/percent: der Wert; currency: Minor Units (Cent). `null` = leer. */
  value: number | null;
  onValueChange: (value: number | null) => void;
  /** ISO-4217, nur bei `currency` (Standard USD). */
  currency?: string;
  /** Einheit rechts („people“, „days“); bei percent „%“. */
  suffix?: string;
  min?: number;
  max?: number;
  /** Schritt für ↑/↓ (Shift: ×10), in Hauptbeträgen. */
  step?: number;
  /** Höchstens so viele Nachkommastellen in der Anzeige (number/percent). */
  maxDigits?: number;
  size?: FieldSize;
}

export function NumberInput({
  ref,
  kind = 'number',
  value,
  onValueChange,
  currency = 'USD',
  suffix,
  min,
  max,
  step = 1,
  maxDigits = 2,
  size = 'md',
  className,
  readOnly,
  onFocus,
  onBlur,
  onKeyDown,
  ...rest
}: NumberInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const field = useFieldProps(rest);
  const isCurrency = kind === 'currency';

  const major = value === null ? null : isCurrency ? fromMinor(value, currency) : value;
  const formatted =
    value === null
      ? ''
      : isCurrency
        ? formatMinorPlain(value, currency)
        : formatPlain(value, maxDigits);
  /** Hauptbetrag → Wert; begrenzt wird erst beim Verlassen, damit Zwischenstände tippbar bleiben. */
  const emit = (next: number | null, bound = false) => {
    if (next === null) return onValueChange(null);
    const result = bound ? clamp(next, min, max) : next;
    onValueChange(isCurrency ? toMinor(result, currency) : result);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || readOnly) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const delta = (event.key === 'ArrowUp' ? 1 : -1) * step * (event.shiftKey ? 10 : 1);
    const next = clamp((major ?? 0) + delta, min, max);
    emit(next, true);
    setDraft(formatPlain(next, isCurrency ? 2 : maxDigits));
  };

  const unit = suffix ?? (kind === 'percent' ? '%' : undefined);
  return (
    <div
      data-size={size}
      data-kind={kind}
      className={cn(
        fieldFrame(size),
        readOnly && 'border-transparent! bg-transparent px-0 focus-within:outline-none',
        className,
      )}
    >
      {isCurrency ? (
        <span aria-hidden="true" className="shrink-0 text-fg-secondary">
          {currencySymbol(currency)}
        </span>
      ) : null}
      <input
        {...rest}
        ref={ref}
        id={field.id}
        required={field.required}
        aria-invalid={field['aria-invalid']}
        aria-describedby={field['aria-describedby']}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        readOnly={readOnly}
        value={draft ?? formatted}
        onFocus={(event: FocusEvent<HTMLInputElement>) => {
          setDraft(formatted);
          onFocus?.(event);
        }}
        onChange={(event) => {
          setDraft(event.target.value);
          emit(parseNumberInput(event.target.value));
        }}
        onBlur={(event) => {
          if (major !== null && clamp(major, min, max) !== major) emit(major, true);
          setDraft(null);
          onBlur?.(event);
        }}
        onKeyDown={handleKeyDown}
        className={cn(fieldInput, 'text-right tabular-nums')}
      />
      {unit ? (
        <span aria-hidden="true" className="shrink-0 text-fg-secondary">
          {unit}
        </span>
      ) : null}
    </div>
  );
}
